/**
 * Horizon Theme - Custom Wishlist (drawer version, no app, no backend)
 * Storage: localStorage (per-browser)
 *
 * Place this file at: assets/wishlist.js
 * Load it in theme.liquid (before </body>) with:
 *   <script src="{{ 'wishlist.js' | asset_url }}" defer></script>
 */
(function () {
  const STORAGE_KEY = 'horizon_wishlist';

  /* ---------- storage helpers ---------- */

  function getWishlist() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(handles) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    document.dispatchEvent(
      new CustomEvent('wishlist:changed', { detail: { count: handles.length, handles } })
    );
  }

  function isInWishlist(handle) {
    return getWishlist().includes(handle);
  }

  function toggleWishlist(handle) {
    let handles = getWishlist();
    const exists = handles.includes(handle);
    if (exists) {
      handles = handles.filter((h) => h !== handle);
    } else {
      handles.push(handle);
    }
    saveWishlist(handles);
    return !exists; // true = added, false = removed
  }

  /* ---------- button state sync (heart icons on cards / product page) ---------- */

  function updateButtonState(button) {
    const handle = button.dataset.productHandle;
    const added = isInWishlist(handle);
    button.classList.toggle('is-active', added);
    button.setAttribute('aria-pressed', added ? 'true' : 'false');
    const label = added ? button.dataset.labelRemove : button.dataset.labelAdd;
    if (label) button.setAttribute('aria-label', label);
  }

  function refreshAllButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(updateButtonState);
  }

  function updateCountBadges() {
    const count = getWishlist().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-hidden', count === 0);
    });
  }

  /* ---------- drawer ---------- */

  const drawer = document.querySelector('[data-wishlist-drawer]');
  const list = document.querySelector('[data-wishlist-list]');
  const emptyState = document.querySelector('[data-wishlist-empty]');

  function openDrawer() {
    if (!drawer) return;
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    document.documentElement.classList.add('wishlist-drawer-locked');
    renderDrawerList();
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('wishlist-drawer-locked');
    // slide-out transition khatam hone ke baad hi hide karo
    window.setTimeout(() => {
      if (!drawer.classList.contains('is-open')) drawer.hidden = true;
    }, 300);
  }

  function formatMoney(cents) {
    try {
      return (cents / 100).toLocaleString(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'INR',
        // No thousands separator (₹1799), as on every other CAI price.
        useGrouping: false,
        // Whole rupees as "₹3199", like every other price on the site, not "₹3199.00".
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
      });
    } catch (e) {
      return '₹' + (cents / 100).toFixed(2);
    }
  }

  async function renderDrawerList() {
    if (!list) return;
    const handles = getWishlist();

    if (handles.length === 0) {
      if (emptyState) emptyState.hidden = false;
      list.innerHTML = '';
      return;
    }
    if (emptyState) emptyState.hidden = true;
    // The product fetches can take seconds on a phone; show that something is happening
    // instead of an empty sheet (iOS Simulator, 24 Sep 2026).
    list.innerHTML = '<li class="wishlist-drawer__loading" aria-live="polite">Loading your wishlist…</li>';

    const products = await Promise.all(
      handles.map((handle) =>
        fetch(`/products/${handle}.js`)
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null)
      )
    );

    list.innerHTML = products
      .filter(Boolean)
      .map((product) => {
        const image = product.featured_image || (product.images && product.images[0]) || '';
        return `
          <li class="wishlist-drawer__item">
            <a href="/products/${product.handle}" class="wishlist-drawer__item-image">
              <img src="${image}" loading="lazy" alt="${product.title}" width="72" height="72">
            </a>
            <div class="wishlist-drawer__item-details">
              <a href="/products/${product.handle}" class="wishlist-drawer__item-title">${product.title}</a>
              <span class="wishlist-drawer__item-price">${formatMoney(product.price)}</span>
            </div>
            <button
              type="button"
              class="wishlist-drawer__item-remove"
              data-wishlist-toggle
              data-product-handle="${product.handle}"
              aria-label="Remove from wishlist"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6 6l12 12M18 6L6 18"/>
              </svg>
            </button>
          </li>
        `;
      })
      .join('');
  }

  /* ---------- event wiring ---------- */

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-wishlist-drawer-open]')) {
      e.preventDefault();
      openDrawer();
      return;
    }

    if (e.target.closest('[data-wishlist-drawer-close]')) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    const button = e.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    e.preventDefault();
    const handle = button.dataset.productHandle;
    if (!handle) return;

    const added = toggleWishlist(handle);

    document
      .querySelectorAll(`[data-wishlist-toggle][data-product-handle="${handle}"]`)
      .forEach(updateButtonState);

    // naya product add hone pe turant confirm ke liye drawer khol do
    if (added && drawer) openDrawer();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer && drawer.classList.contains('is-open')) {
      closeDrawer();
    }
  });

  document.addEventListener('wishlist:changed', function () {
    updateCountBadges();
    if (drawer && drawer.classList.contains('is-open')) renderDrawerList();
  });

  document.addEventListener('DOMContentLoaded', function () {
    refreshAllButtons();
    updateCountBadges();
  });

  document.addEventListener('shopify:section:load', function () {
    refreshAllButtons();
    updateCountBadges();
  });

  window.Wishlist = {
    get: getWishlist,
    isInWishlist: isInWishlist,
    toggle: toggleWishlist,
    open: openDrawer,
    close: closeDrawer,
  };
})();